import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {AxiosError} from "axios";
import useAlertMessage from "hooks/useAlertMessage";
import pxToRem from "hooks/usePxToRem";
import {Dispatch, SetStateAction, useState} from "react";
import {
  GetProcessById,
  GetProcessFiles,
  PostProcessFile,
} from "services/api_v2/Processes/Processes.service";
import {CustomButton, CustomUploader} from "ui";
import {Grid} from "@mui/system";

interface AttachFilesProps {
  emailData: {
    subject: string;
    to: string[];
    cc: string[];
    bcc: string[];
    attachment: {name: string; url: string; id: string; guid: string}[];
  };
  setEmailData: Dispatch<
    SetStateAction<{
      subject: string;
      to: string[];
      cc: string[];
      bcc: string[];
      attachment: {name: string; url: string; id: string; guid: string}[];
    }>
  >;
  processId: number;
  disabled?: boolean;
}

type File = {
  originalName: string;
  url: string;
  id: string;
  guid: string;
  groupFile: {name: string; id: number; isActive: boolean};
  idGroupFile: number;
};

const AttachFiles = (props: AttachFilesProps) => {
  const {disabled = false} = props;
  const ID_OTHER_DOCUMENTS = 50; //id para "outros documentos"
  const [showAlert] = useAlertMessage();

  const [showUploader, setShowUploader] = useState(false);

  const handleShowUploader = () => {
    setShowUploader((state) => !state);
    setShowFilesList(false);
  };

  const getFilesFromProcess = async () => {
    try {
      const {data} = await GetProcessFiles(props.processId, ID_OTHER_DOCUMENTS);
      return Array.isArray(data.data) ? data.data : [];
    } catch (error) {
      console.error(error);
      showAlert("Erro ao trazer documentos do processo");
      return [];
    }
  };

  const uploadFilesToProcess = async (file) => {
    const formData = new FormData();

    formData.append("file", file);
    formData.append(
      "process",
      JSON.stringify({
        description: file?.name,
        idGroupFile: ID_OTHER_DOCUMENTS,
        idProcess: props.processId,
      }),
    );
    try {
      const result = await PostProcessFile(formData);
      return result;
    } catch (error: any) {
      const err = error as AxiosError;
      showAlert(err?.response?.data?.message);
      console.error(error);
    }
  };

  const handleAttachFileToEmail = async (acceptedFiles) => {
    const fileUploaded = await uploadFilesToProcess(acceptedFiles[0]);

    const fileUploadedHash = fileUploaded?.data?.fileUploaded?.hash;

    const processAttachments = await getFilesFromProcess();

    const attachedFile = processAttachments.find(
      (item) => item?.file?.hash === fileUploadedHash,
    );

    const lastAttachedFileData = {
      name: attachedFile?.file?.originalName ?? "",
      url: attachedFile?.file?.url ?? "",
      id: attachedFile?.file?.id?.toString() ?? "",
      guid: attachedFile?.file?.guid ?? "",
    };

    props.setEmailData((state) => ({
      ...state,
      attachment: [...state.attachment, lastAttachedFileData],
    }));

    handleShowUploader();
  };

  const [processFilesList, setProcessFilesList] = useState([] as File[]);
  const [showFilesList, setShowFilesList] = useState(false);

  const formatFilesByAlphabeticOrder = (files: File[]) => {
    return files?.sort((a, b) =>
      a.groupFile?.name?.localeCompare(b.groupFile?.name),
    );
  };

  const getAllFilesFromProcess = async () => {
    try {
      const response = await GetProcessById(props.processId);

      const filesNotAttached = response.data.process.files.filter(
        (item) => !props.emailData.attachment?.some((i) => i.id === item?.id),
      );

      setProcessFilesList(formatFilesByAlphabeticOrder(filesNotAttached));
    } catch (error: any) {
      console.error(error);
      showAlert(error?.response?.data?.message);
    }
  };

  const handleShowFilesList = () => {
    setShowFilesList((state) => !state);
    setShowUploader(false);

    if (processFilesList.length > 1) {
      setProcessFilesList([]);
    } else {
      getAllFilesFromProcess();
    }
  };

  const handleAddProcessFileToEmailAttachments = (
    selectedFile: {
      name: string;
      url: string;
      id: string;
      guid: string;
    }[],
  ) => {
    props.setEmailData((state) => ({
      ...state,
      attachment: [...state.attachment, ...selectedFile],
    }));

    handleShowFilesList();
    setSelectedFiles([]);
  };

  const renderFileGroup = (item: File) => {
    return item?.groupFile?.name;
  };

  const [selectedFiles, setSelectedFiles] = useState<
    {name: string; url: string; id: string; guid: string}[]
  >([]);

  const handleSelectFile = (
    event,
    file: {name: string; url: string; id: string; guid: string},
  ) => {
    if (event.target.checked) {
      setSelectedFiles((prev) => [...prev, file]);
    } else {
      setSelectedFiles((prev) => prev.filter((item) => item.id !== file.id));
    }
  };

  return (
    <Grid
      container
      sx={{justifyContent: "space-between", marginBottom: pxToRem(8)}}
    >
      <Grid size={6.5}>
        <CustomButton
          dataTestId="btn-attach-from-process"
          variant="outlined"
          size="small"
          onClickFn={handleShowFilesList}
          sx={{fontSize: "12px", width: "100%"}}
          disabled={disabled}
        >
          Anexar do processo
        </CustomButton>
      </Grid>
      <Grid size={5}>
        <CustomButton
          dataTestId="btn-attach-new"
          variant="outlined"
          size="small"
          onClickFn={handleShowUploader}
          sx={{fontSize: "12px", width: "100%"}}
          disabled={disabled}
        >
          Anexar novo
        </CustomButton>
      </Grid>

      {showFilesList && processFilesList.length > 0 && (
        <Stack alignItems={"center"}>
          <FormGroup
            data-testid="files-options"
            aria-labelledby="radio-buttons-group"
          >
            {processFilesList?.map((item) => (
              <Tooltip
                key={item?.id}
                title="Grupo do arquivo - Nome do arquivo"
                placement="right"
                arrow
              >
                <FormControlLabel
                  value={{
                    id: item?.id,
                    name: item?.originalName,
                    url: item?.url,
                    guid: item?.guid,
                  }}
                  onChange={(e) =>
                    handleSelectFile(e, {
                      id: item?.id,
                      name: item?.originalName,
                      url: item?.url,
                      guid: item?.guid,
                    })
                  }
                  control={<Checkbox size="small" />}
                  label={
                    <Typography sx={{fontSize: "11px"}}>
                      {renderFileGroup(item)} - {item?.originalName}
                    </Typography>
                  }
                />
              </Tooltip>
            ))}
          </FormGroup>
          <CustomButton
            onClickFn={() =>
              handleAddProcessFileToEmailAttachments(selectedFiles)
            }
            size="small"
            sx={{fontSize: pxToRem(12), marginTop: pxToRem(8), width: "50%"}}
          >
            Anexar selecionados
          </CustomButton>
        </Stack>
      )}
      {showFilesList && !processFilesList.length && (
        <Typography
          data-testid="info-no-available-files"
          sx={{
            width: "100%",
            fontSize: "11px",
            textAlign: "center",
            marginTop: pxToRem(8),
          }}
        >
          Nenhum documento do processo disponível
        </Typography>
      )}
      {showUploader && (
        <Grid data-testid="file-uploader" size={12}>
          <CustomUploader
            singleFile
            handleSetDocuments={(acceptedFiles) =>
              handleAttachFileToEmail(acceptedFiles)
            }
          />
        </Grid>
      )}
    </Grid>
  );
};

export default AttachFiles;
